import { useState } from 'react'
import { CalendarPlus } from 'lucide-react'
import { Button, EndpointHint, FORM_INPUT_CLASS, FormError, HelpIcon, LoadFailure, WORKER_ROLE_LABELS, type WorkerRole } from '../../core/index.js'
import { schedulingRulesOf, useFacilityConfig, type SchedulingRules, type ShiftTypeKey } from '../../facilities/index.js'
import { useAvailableShifts, usePublishShifts } from '../hooks.js'
import type { AvailableShift, PublishShiftInput } from '../types.js'
import { describeDay } from './preferencesText.js'
import { todayDate } from './scheduleCalendarData.js'
import { datesBetween, defaultPublishForm, MAX_CAPACITY, parseUnits, publishBatches, publishBlocker, publishedInRange, publishInputsOf, type PublishShiftsForm } from './publishShiftsData.js'

export interface PublishShiftsCardProps {
  rules: SchedulingRules
  /** The facility's published shifts, for the defaults and the note on dates already published. */
  existing: readonly AvailableShift[]
  /** Publishes one part; answers how many seats it wrote (a seat a worker holds is never written again). */
  publish: (part: PublishShiftInput[]) => Promise<number>
  /** The call the form makes, printed for a SeeWhoCares admin. */
  endpoint: string
}

const ROLES = Object.keys(WORKER_ROLE_LABELS) as WorkerRole[]

type Outcome = { written: number; from: string; to: string }

/**
 * Publishing a run of the facility's shifts: every date from the first to
 * the last, on each unit, of each chosen shift type at the hours the
 * facility's catalogue gives it, with a number of seats and an optional
 * role. What workers bid for and the month is built from; publishing the
 * same dates again never takes a seat from the worker holding it.
 */
export function PublishShiftsCard({ rules, existing, publish, endpoint }: PublishShiftsCardProps) {
  const today = todayDate()
  const [form, setForm] = useState<PublishShiftsForm>(() => defaultPublishForm(rules, existing, today))
  const [unitsText, setUnitsText] = useState(form.units.join(', '))
  const [progress, setProgress] = useState<{ done: number; of: number } | null>(null)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [error, setError] = useState<unknown>(null)

  const catalogue = rules.shiftTypes.filter((entry) => entry.enabled)
  const configuredUnits = rules.units
  const blocker = publishBlocker(form, today)
  const inputs = blocker ? [] : publishInputsOf(form, rules)
  const seats = inputs.length * form.capacity
  const already = blocker ? 0 : publishedInRange(form, existing)
  const busy = progress !== null

  const change = (next: Partial<PublishShiftsForm>): void => {
    setForm((current) => ({ ...current, ...next }))
    setOutcome(null)
    setError(null)
  }
  const toggleType = (key: ShiftTypeKey): void => change({ shiftTypes: form.shiftTypes.includes(key) ? form.shiftTypes.filter((entry) => entry !== key) : [...form.shiftTypes, key] })
  // Kept in the configuration's order, so the published shifts read the same way round whatever order they were clicked in.
  const toggleUnit = (id: string): void => change({ units: configuredUnits.map((unit) => unit.id).filter((entry) => (entry === id ? !form.units.includes(id) : form.units.includes(entry))) })

  async function submit(): Promise<void> {
    const parts = publishBatches(inputs)
    setError(null)
    setOutcome(null)
    setProgress({ done: 0, of: parts.length })
    let written = 0
    try {
      for (const [index, part] of parts.entries()) {
        written += await publish(part)
        setProgress({ done: index + 1, of: parts.length })
      }
      setOutcome({ written, from: form.from, to: form.to })
    } catch (failure) {
      setError(failure)
      // What went up before the failure stays published; saying so lets the reader publish the same dates again safely.
      if (written > 0) {
        setOutcome({ written, from: form.from, to: form.to })
      }
    } finally {
      setProgress(null)
    }
  }

  return (
    <div className="bg-white border border-marine/15 rounded p-4 flex flex-col gap-3">
      <div>
        <div className="text-sm font-black text-ink">
          Publish shifts
          <HelpIcon topic="schedule.publish-shifts" className="ml-1" />
          <EndpointHint className="ml-2">{endpoint}</EndpointHint>
        </div>
        <p className="text-[11px] text-ink-mid max-w-xl leading-relaxed">
          Open the shifts workers bid for and the month is built from: every date in the run, on each unit, of each shift type at this facility's hours. Publishing dates again never takes a seat from the worker who holds it.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-xs font-bold text-ink-mid">
          First date
          <input type="date" className={`${FORM_INPUT_CLASS} mt-1`} value={form.from} min={today} disabled={busy} onChange={(event) => change({ from: event.target.value })} />
        </label>
        <label className="text-xs font-bold text-ink-mid">
          Last date
          <input type="date" className={`${FORM_INPUT_CLASS} mt-1`} value={form.to} min={form.from || today} disabled={busy} onChange={(event) => change({ to: event.target.value })} />
        </label>
        <div className="sm:col-span-2">
          <div className="text-xs font-bold text-ink-mid mb-1">Shift types</div>
          <div role="group" aria-label="Shift types" className="flex flex-wrap gap-1.5">
            {catalogue.map((entry) => {
              const on = form.shiftTypes.includes(entry.key)
              return (
                <button key={entry.key} type="button" aria-pressed={on} disabled={busy} onClick={() => toggleType(entry.key)} className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${on ? 'border-marine bg-marine text-white' : 'border-marine/30 text-ink-mid hover:border-marine'}`}>
                  {entry.label} <span className="font-normal">{entry.start}–{entry.end}</span>
                </button>
              )
            })}
          </div>
          {catalogue.length === 0 && <p className="mt-1 text-[11px] text-ink-mid">This facility offers no shift types. Switch one on in Configuration › Scheduling.</p>}
        </div>
        {configuredUnits.length > 0 ? (
          <div>
            <div className="text-xs font-bold text-ink-mid mb-1">Units</div>
            <div role="group" aria-label="Units" className="flex flex-wrap gap-1.5">
              {configuredUnits.map((unit) => {
                const on = form.units.includes(unit.id)
                return (
                  <button key={unit.id} type="button" aria-pressed={on} disabled={busy} onClick={() => toggleUnit(unit.id)} className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${on ? 'border-marine bg-marine text-white' : 'border-marine/30 text-ink-mid hover:border-marine'}`}>
                    {unit.name}
                  </button>
                )
              })}
            </div>
            <span className="mt-1 block text-[11px] text-ink-mid">From Configuration › Details; each unit chosen gets every shift.</span>
          </div>
        ) : (
          <label className="text-xs font-bold text-ink-mid">
            Units
            <input
              type="text"
              className={`${FORM_INPUT_CLASS} mt-1`}
              value={unitsText}
              disabled={busy}
              placeholder="unit-a, unit-b"
              onChange={(event) => {
                setUnitsText(event.target.value)
                change({ units: parseUnits(event.target.value) })
              }}
            />
            <span className="mt-1 block text-[11px] font-normal">Separated by commas; each unit gets every shift. List the facility's units in Configuration › Details to choose from them here.</span>
          </label>
        )}
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-bold text-ink-mid">
            Seats per shift
            <input type="number" className={`${FORM_INPUT_CLASS} mt-1`} min={1} max={MAX_CAPACITY} step={1} value={form.capacity} disabled={busy} onChange={(event) => change({ capacity: Number(event.target.value) })} />
          </label>
          <label className="text-xs font-bold text-ink-mid">
            Role
            <select className={`${FORM_INPUT_CLASS} mt-1`} value={form.role ?? ''} disabled={busy} onChange={(event) => change({ role: (event.target.value || null) as WorkerRole | null })}>
              <option value="">Any role</option>
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-marine/10 pt-3">
        <div className="text-[11px] text-ink-mid leading-relaxed">
          {blocker ? (
            <span className="font-bold text-coral">{blocker}</span>
          ) : (
            <>
              <span className="font-bold text-ink">
                {seats} {seats === 1 ? 'seat' : 'seats'}
              </span>{' '}
              over {datesBetween(form.from, form.to).length} days, {describeDay(form.from)} to {describeDay(form.to)}.
              {already > 0 && <> {already} {already === 1 ? 'seat is' : 'seats are'} already published in these dates: any a worker holds is kept, the rest are published again with these hours.</>}
            </>
          )}
        </div>
        <Button icon={CalendarPlus} disabled={busy || blocker !== null} onClick={() => void submit()}>
          {progress ? `Publishing… ${progress.done} of ${progress.of}` : 'Publish shifts'}
        </Button>
      </div>

      {outcome && (
        <p role="status" className="text-[11px] font-bold text-green">
          Published {outcome.written} {outcome.written === 1 ? 'seat' : 'seats'}, {describeDay(outcome.from)} to {describeDay(outcome.to)}.
        </p>
      )}
      {error !== null && <FormError error={error} fallback="The shifts could not be published. Publishing the same dates again is safe." />}
    </div>
  )
}

/** The Publish shifts card on a facility's Dashboard › Month tab, for its staff: the facility's rules and published shifts read, each part sent as the staff member. */
export function FacilityPublishShifts({ facilityId }: { facilityId: string }) {
  const config = useFacilityConfig(facilityId)
  const shifts = useAvailableShifts(facilityId)
  const publish = usePublishShifts(facilityId)
  if (config.isPending || shifts.isPending) {
    return null
  }
  if (config.isError || shifts.isError || !config.data) {
    return <LoadFailure message="The shifts form could not be loaded." onRetry={() => void Promise.all([config.refetch(), shifts.refetch()])} retrying={config.isFetching || shifts.isFetching} />
  }
  return <PublishShiftsCard key={facilityId} rules={schedulingRulesOf(config.data)} existing={shifts.data ?? []} publish={async (part) => (await publish.mutateAsync(part)).length} endpoint="POST /facilities/{facilityId}/schedule/shifts" />
}
